import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Crown, AlertTriangle, FileText, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import StatusBadge from "../StatusBadge";

// Transfer Department Dialog
export function TransferDialog({
  open,
  onOpenChange,
  application,
  transferOfficials,
  selectedTransferOfficial,
  setSelectedTransferOfficial,
  transferNote,
  setTransferNote,
  transferLoading,
  getTargetDepartment,
  onTransfer,
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("applicationDetail.transferTitle")}</DialogTitle>
          <DialogDescription>
            {t("applicationDetail.transferDescription", {
              department:
                getTargetDepartment() === "survey"
                  ? t("department.survey")
                  : t("department.revenue"),
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-stone-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-slate-700">
              #{application?.land_survey_number}
            </p>
            <p className="text-xs text-slate-500">{application?.farmer_name}</p>
          </div>

          <div>
            <Label className="text-sm font-medium">
              {t("applicationDetail.assignToOfficial")}
            </Label>
            <Select
              value={selectedTransferOfficial}
              onValueChange={setSelectedTransferOfficial}
            >
              <SelectTrigger className="mt-2">
                <SelectValue
                  placeholder={t("applicationDetail.selectOfficialOptional")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t("applicationDetail.leaveUnassigned")}
                </SelectItem>
                {transferOfficials.map((official) => (
                  <SelectItem key={official.id} value={official.id}>
                    {official.name} ({t(`designation.${official.designation}`)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              {t("applicationDetail.optionalAssignment")}
            </p>
          </div>

          {/* Transfer Note Section */}
          <div>
            <Label className="text-sm font-medium">
              {t("applicationDetail.transferNote") || "Transfer Note"} <span className="text-red-500">*</span>
            </Label>
            <Textarea
              value={transferNote}
              onChange={(e) => setTransferNote(e.target.value)}
              placeholder={t("applicationDetail.transferNotePlaceholder") || "Please provide the reason for transferring this application..."}
              className="mt-2 min-h-[100px]"
              data-testid="transfer-note-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              {t("applicationDetail.transferNoteHelp") || "This note will be recorded in the application history."}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onTransfer}
            disabled={transferLoading || !transferNote?.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {transferLoading
              ? t("common.processing")
              : t("applicationDetail.confirmTransfer")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// GOK Override Dialog
export function OverrideDialog({
  open,
  onOpenChange,
  application,
  overrideStatus,
  setOverrideStatus,
  overrideReason,
  setOverrideReason,
  overrideLoading,
  onOverride,
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <Crown className="w-5 h-5" />
            {t("gok.overrideDecision")}
          </DialogTitle>
          <DialogDescription>{t("gok.overrideDescription")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Warning Banner */}
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">{t("gok.overrideWarningTitle")}</p>
              <p className="text-xs mt-1">{t("gok.overrideWarningDesc")}</p>
            </div>
          </div>

          {/* Application Info */}
          <div className="bg-stone-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-slate-700">
              #{application?.land_survey_number}
            </p>
            <p className="text-xs text-slate-500">{application?.farmer_name}</p>
            <div className="mt-2">
              <StatusBadge status={application?.status} />
            </div>
          </div>

          {/* New Status Selection */}
          <div>
            <Label className="text-sm font-medium">{t("gok.newStatus")}</Label>
            <Select value={overrideStatus} onValueChange={setOverrideStatus}>
              <SelectTrigger className="mt-2" data-testid="override-status-select">
                <SelectValue placeholder={t("gok.selectNewStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved" className="text-emerald-700">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {t("status.approved")}
                  </div>
                </SelectItem>
                <SelectItem value="rejected" className="text-red-700">
                  {t("status.rejected")}
                </SelectItem>
                <SelectItem value="submitted">{t("status.submitted")}</SelectItem>
                <SelectItem value="assigned">{t("status.assigned")}</SelectItem>
                <SelectItem value="document_verification">
                  {t("status.document_verification")}
                </SelectItem>
                <SelectItem value="field_verification">
                  {t("status.field_verification")}
                </SelectItem>
                <SelectItem value="under_review">
                  {t("status.under_review")}
                </SelectItem>
                <SelectItem value="survey_inspection">
                  {t("status.survey_inspection")}
                </SelectItem>
                <SelectItem value="boundary_verification">
                  {t("status.boundary_verification")}
                </SelectItem>
                <SelectItem value="sketch_preparation">
                  {t("status.sketch_preparation")}
                </SelectItem>
                <SelectItem value="order_issued">
                  {t("status.order_issued")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div>
            <Label className="text-sm font-medium">{t("gok.overrideReason")}</Label>
            <textarea
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder={t("gok.overrideReasonPlaceholder")}
              className="mt-2 w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              rows={3}
              data-testid="override-reason-input"
            />
            <p className="text-xs text-slate-500 mt-1">
              {t("gok.overrideReasonHint")}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onOverride}
            disabled={overrideLoading || !overrideStatus || !overrideReason.trim()}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="confirm-override-btn"
          >
            {overrideLoading ? t("common.processing") : t("gok.confirmOverride")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Section Assignment Dialog
export function SectionDialog({
  open,
  onOpenChange,
  application,
  sections,
  circles,
  surveyOfficeTypes = [],
  selectedSection,
  setSelectedSection,
  selectedCircle,
  setSelectedCircle,
  selectedSurveyOfficeType,
  setSelectedSurveyOfficeType,
  sectionComment,
  setSectionComment,
  sectionLoading,
  onAssign,
  showCircleField = true,
  isSurveyUser = false,
}) {
  const { t } = useTranslation();

  // Determine if the assign button should be disabled
  const isAssignDisabled = () => {
    if (sectionLoading) return true;
    if (isSurveyUser) {
      return !selectedSurveyOfficeType;
    }
    return !selectedSection || (showCircleField && !selectedCircle);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <FileText className="w-5 h-5" />
            {isSurveyUser 
              ? (t("surveyOffice.assignOffice") || "Assign Office Type")
              : t("section.assignSection")}
          </DialogTitle>
          <DialogDescription>
            {isSurveyUser
              ? (t("surveyOffice.assignDescription") || "Assign this application to Survey Office or Field Office for further processing.")
              : showCircleField 
                ? (t("section.assignDescription") || "Assign this application to a section and circle for further processing.")
                : (t("section.assignDescriptionSurvey") || "Assign this application to a section for further processing.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Application Info */}
          <div className="bg-stone-50 p-3 rounded-lg">
            <p className="text-sm font-medium text-slate-700">
              #{application?.land_survey_number}
            </p>
            <p className="text-xs text-slate-500">{application?.farmer_name}</p>
          </div>

          {/* Survey Office Type Selection - Only for Survey users */}
          {isSurveyUser ? (
            <div>
              <Label className="text-sm font-medium">
                {t("surveyOffice.selectOfficeType") || "Select Office Type"}
              </Label>
              <Select value={selectedSurveyOfficeType} onValueChange={setSelectedSurveyOfficeType}>
                <SelectTrigger className="mt-2" data-testid="survey-office-type-select">
                  <SelectValue placeholder={t("surveyOffice.selectOfficeType") || "Select Office Type"} />
                </SelectTrigger>
                <SelectContent>
                  {surveyOfficeTypes.map((officeType) => (
                    <SelectItem key={officeType.value} value={officeType.value}>
                      {officeType.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-slate-500">
                {t("surveyOffice.officeTypeHint") || "Survey Office: ADLR, Case Worker, Supervisor, Superintendent | Field Office: Surveyor"}
              </p>
            </div>
          ) : (
            <>
              {/* Section Selection - For Revenue users */}
              <div>
                <Label className="text-sm font-medium">
                  {t("section.selectSection")}
                </Label>
                <Select value={selectedSection} onValueChange={setSelectedSection}>
                  <SelectTrigger className="mt-2" data-testid="section-select">
                    <SelectValue placeholder={t("section.selectSection")} />
                  </SelectTrigger>
                  <SelectContent>
                    {sections.map((section) => (
                      <SelectItem key={section.value} value={section.value}>
                        {section.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Circle Selection - For Revenue users */}
              {showCircleField && (
                <div>
                  <Label className="text-sm font-medium">
                    {t("circle.selectCircle")}
                  </Label>
                  <Select value={selectedCircle} onValueChange={setSelectedCircle}>
                    <SelectTrigger className="mt-2" data-testid="circle-select">
                      <SelectValue placeholder={t("circle.selectCircle")} />
                    </SelectTrigger>
                    <SelectContent>
                      {circles.map((circle) => (
                        <SelectItem key={circle.value} value={circle.value}>
                          {circle.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}

          {/* Comment */}
          <div>
            <Label className="text-sm font-medium">
              {t("comments.addComment")} ({t("common.optional")})
            </Label>
            <textarea
              value={sectionComment}
              onChange={(e) => setSectionComment(e.target.value)}
              placeholder={t("comments.placeholder")}
              className="mt-2 w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              rows={2}
              data-testid="section-comment-input"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onAssign}
            disabled={isAssignDisabled()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="confirm-section-btn"
          >
            {sectionLoading ? t("common.processing") : t("common.assign")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Edit Application Dialog
export function EditDialog({
  open,
  onOpenChange,
  editFormData,
  onFormChange,
  editLoading,
  onSave,
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-emerald-700">
            {t("applicationDetail.editTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("applicationDetail.editDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="land_survey_number">
              {t("newApplication.surveyNumber")}
            </Label>
            <Input
              id="land_survey_number"
              value={editFormData.land_survey_number || ""}
              onChange={(e) => onFormChange("land_survey_number", e.target.value)}
              data-testid="edit-survey-number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="land_area">{t("newApplication.landArea")}</Label>
            <Input
              id="land_area"
              type="number"
              step="0.01"
              value={editFormData.land_area || ""}
              onChange={(e) =>
                onFormChange("land_area", parseFloat(e.target.value) || 0)
              }
              data-testid="edit-land-area"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="purpose">{t("newApplication.purpose")}</Label>
            <Textarea
              id="purpose"
              value={editFormData.purpose || ""}
              onChange={(e) => onFormChange("purpose", e.target.value)}
              rows={3}
              data-testid="edit-purpose"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">{t("newApplication.location")}</Label>
            <Input
              id="location"
              value={editFormData.location || ""}
              onChange={(e) => onFormChange("location", e.target.value)}
              data-testid="edit-location"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="district">{t("newApplication.district")}</Label>
              <Input
                id="district"
                value={editFormData.district || ""}
                onChange={(e) => onFormChange("district", e.target.value)}
                data-testid="edit-district"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taluk">{t("newApplication.taluk")}</Label>
              <Input
                id="taluk"
                value={editFormData.taluk || ""}
                onChange={(e) => onFormChange("taluk", e.target.value)}
                data-testid="edit-taluk"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hobli">{t("newApplication.hobli")}</Label>
              <Input
                id="hobli"
                value={editFormData.hobli || ""}
                onChange={(e) => onFormChange("hobli", e.target.value)}
                data-testid="edit-hobli"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="village">{t("newApplication.village")}</Label>
              <Input
                id="village"
                value={editFormData.village || ""}
                onChange={(e) => onFormChange("village", e.target.value)}
                data-testid="edit-village"
              />
            </div>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="edit-cancel-btn"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={onSave}
            disabled={editLoading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="edit-save-btn"
          >
            {editLoading
              ? t("common.processing")
              : t("applicationDetail.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Delete Confirmation Dialog
export function DeleteDialog({
  open,
  onOpenChange,
  application,
  deleteLoading,
  onDelete,
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {t("applicationDetail.deleteTitle")}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {t("applicationDetail.deleteDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>{t("application.surveyNumber")}:</strong>{" "}
              {application?.land_survey_number}
            </p>
            <p className="text-sm text-red-800 mt-1">
              <strong>{t("application.purpose")}:</strong> {application?.purpose}
            </p>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="delete-cancel-btn"
          >
            {t("applicationDetail.deleteCancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={onDelete}
            disabled={deleteLoading}
            data-testid="delete-confirm-btn"
          >
            {deleteLoading
              ? t("common.processing")
              : t("applicationDetail.deleteConfirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
